import { ReactComponent as SensorIconS } from '@assets/components/panel/monitoring/sensor-icon-S.svg';
import { IcChevronBackNegative, IcParameters } from '@assets/icons/24x24';
import { Box, HStack, Icon, IconButton, Tabs, Text, Textarea, VStack } from '@chakra-ui/react';
import CommonMaintenanceActions from '@components/map/popup/infos/sensors/maintenance/CommonMaintenanceActions';
import useClickOutside from '@hooks/useClickOutside';
import { getSensorHexColor, isSensorConfiguration } from '@utils/sensors/sensors.utils';
import { FormattedMessage } from 'react-intl';

import { SubSensorTypeEnum } from '@/types/sensor/sensors';

import { monitoringTypeFromSensorType } from '../monitoring.types';
import { isNotSectionTypeSensorOrSubInfo } from '../monitoring.utils';
import { useMonitoringContext, useSelectedSensorKey } from '../MonitoringContext';
import AvailabilityTab from './availability/AvailabilityTab';
import GlobalTab from './global/GlobalTab';

export default function SensorDetail() {
  const { setSelectedSensorKey, filteredSensorOrSubSensorInfoList } = useMonitoringContext();
  const { ref, isOpen, toggle } = useClickOutside();
  const selectedSensorKey = useSelectedSensorKey();
  const sensorOrSubSensorInfo = filteredSensorOrSubSensorInfoList.find(
    (sensorInfo) => sensorInfo.configuration.uniqueKey === selectedSensorKey,
  );

  const sensorType = sensorOrSubSensorInfo?.configuration.type;

  if (
    sensorOrSubSensorInfo === undefined ||
    sensorType === undefined ||
    sensorType === SubSensorTypeEnum.AUTOMATON_SEGMENT ||
    !isNotSectionTypeSensorOrSubInfo(sensorOrSubSensorInfo)
  ) {
    setSelectedSensorKey(null);
    return null;
  }

  const sensorText = JSON.stringify(sensorOrSubSensorInfo.status, null, '\t');

  return (
    <Tabs.Root defaultValue="global" size="md" lazyMount variant="light" userSelect="none">
      <HStack width="100%" position="relative" gap={0} userSelect="none" backgroundColor="neutral.900">
        <IconButton variant="transparent" boxSize="56px" aria-label="back" onClick={() => setSelectedSensorKey(null)}>
          <Icon as={IcChevronBackNegative} boxSize="24px" />
        </IconButton>
        <HStack gap={1}>
          <Icon as={SensorIconS} boxSize="28px" color={getSensorHexColor(sensorOrSubSensorInfo)} />
          <Text color="neutral.200">{monitoringTypeFromSensorType[sensorType]}</Text>
          <Text
            padding={0.25}
            backgroundColor="neutral.black"
            textStyle="xs"
            fontWeight="medium"
            textDecoration="uppercase"
          >
            {sensorOrSubSensorInfo.configuration.name}
          </Text>
        </HStack>
        <Tabs.List width="fit-content" position="absolute" left="50%" transform="translate(-50%)">
          <Tabs.Trigger value="global">
            <FormattedMessage id="components.monitoring.sensorDetail.global.title" />
          </Tabs.Trigger>
          <Tabs.Trigger value="availability">
            <FormattedMessage id="components.monitoring.sensorDetail.availability.title" />
          </Tabs.Trigger>
          <Tabs.Trigger value="status">
            <FormattedMessage id="components.monitoring.sensorDetail.status" />
          </Tabs.Trigger>
        </Tabs.List>
        <Box marginLeft="auto" position="relative" ref={ref}>
          <IconButton
            variant="transparent"
            boxSize="56px"
            backgroundColor={isOpen ? 'neutral.800' : undefined}
            color={isOpen ? 'neutral.white' : undefined}
            data-active={isOpen}
            onClick={toggle}
          >
            <Icon as={IcParameters} boxSize="24px" />
          </IconButton>
          {isOpen && (
            <Box
              position="fixed"
              width="460px"
              border="2px solid"
              borderColor="neutral.black"
              backgroundColor="neutral.850"
              top="124px"
              right="70px"
              zIndex={2}
            >
              <CommonMaintenanceActions sensorOrSubSensorInfo={sensorOrSubSensorInfo} replayMode={false} />
            </Box>
          )}
        </Box>
      </HStack>
      <Tabs.ContentGroup>
        <Tabs.Content value="global">
          <GlobalTab sensorOrSubSensorInfo={sensorOrSubSensorInfo} />
        </Tabs.Content>
        <Tabs.Content value="status">
          <VStack width="100%" backgroundColor="neutral.700" flex={1} paddingX="180px" paddingY={5} gap={2}>
            <Textarea
              width="100%"
              height="100%"
              spellCheck="false"
              flex={1}
              value={sensorText}
              disabled
              fontSize="14px"
              backgroundColor="neutral.900"
              resize="none"
              className="cockpit-scrollbar-900"
            />
          </VStack>
        </Tabs.Content>
        <Tabs.Content value="availability">
          {isSensorConfiguration(sensorOrSubSensorInfo.configuration) && (
            <AvailabilityTab
              uniqueKey={sensorOrSubSensorInfo.configuration.uniqueKey}
              type={sensorOrSubSensorInfo.configuration.type}
            />
          )}
        </Tabs.Content>
      </Tabs.ContentGroup>
    </Tabs.Root>
  );
}
